import { useEffect, useId, useState, type FormEvent } from 'react'
import { ApiError } from '../api/client'
import { fetchSessionUser, signInWithPassword } from '../auth/auth-client'
import { startMicrosoftLogin } from '../auth/auth-context'
import { useAuthMethods } from '../auth/use-auth-methods'
import { Button } from '../components/primitives/Button'
import { BootSplash } from '../components/shell/BootSplash'

// Read per render (not at module load) so tests can stub the build flag.
function microsoftAuthEnabled(): boolean {
  return import.meta.env.VITE_MICROSOFT_AUTH_ENABLED === 'true'
}

// 6 is intentional during the dev-password-auth bridge: every seeded
// account's password equals the local-part of its email (see
// services/api/src/seed.py::dev_password_for). Local-parts are
// guaranteed ≥6 chars (named entries are all ≥8; the procedural cohort
// pads with a `.NNN` suffix so even the shortest pool entry "hao"
// becomes "hao.001" = 7 chars). Once Microsoft Entra is the only IdP,
// this whole password code path goes away.
const MIN_PASSWORD_LENGTH = 6
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface FieldErrors {
  email?: string
  password?: string
}

function validate(email: string, password: string): FieldErrors {
  const errors: FieldErrors = {}
  if (!email.trim()) {
    errors.email = 'Email is required.'
  } else if (!EMAIL_PATTERN.test(email.trim())) {
    errors.email = 'Enter a valid email address.'
  }
  if (!password) {
    errors.password = 'Password is required.'
  } else if (password.length < MIN_PASSWORD_LENGTH) {
    errors.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`
  }
  return errors
}

function getErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    return err.message
  }
  if (typeof err === 'string') {
    return err
  }
  return 'Sign-in failed. Please try again.'
}

/**
 * Read the `?next=` deep-link target safely.
 *
 * Open-redirect protection (SEC LOW-01, #207). This used to string-match the
 * prefix: accept a leading `/`, reject a leading `//`. Browsers normalise a
 * backslash to a path separator in http(s) URLs, so `/\evil.com` passed that
 * guard and then resolved to `https://evil.com/` — a phishing landing reached
 * from the real login page, or the redirect leg of an OAuth-code-theft chain.
 *
 * The fix is to stop pattern-matching the string and ask the URL parser,
 * which normalises exactly the way the browser will when it performs the
 * navigation. Only the path is returned, never the caller's string, so
 * anything that resolves off-origin cannot survive as a redirect target.
 *
 * Exported for tests.
 */
export function readNextRedirect(): string {
  if (typeof window === 'undefined') return '/'
  const params = new URLSearchParams(window.location.search)
  const next = params.get('next')
  if (!next) return '/'
  try {
    const url = new URL(next, window.location.origin)
    if (url.origin !== window.location.origin) return '/'
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return '/'
  }
}

/**
 * Map the `?error=` code set by the api's OIDC callback redirect
 * (services/api/src/routes/auth.py::_login_error_redirect) to friendly
 * copy. Unknown codes get generic copy — never echo the raw param.
 */
function readSsoError(): string | null {
  if (typeof window === 'undefined') return null
  const code = new URLSearchParams(window.location.search).get('error')
  if (!code) return null
  switch (code) {
    case 'unknown_user':
      return "We couldn't find a Leave Hub account for your Microsoft sign-in. Please contact HR to get onboarded."
    case 'no_employee':
      return "Your sign-in isn't linked to an active employee record. Please contact HR."
    case 'entra_failed':
    case 'missing_email':
      return "Microsoft sign-in didn't complete. Please try again — if it keeps happening, contact HR."
    default:
      return 'Sign-in failed. Please try again.'
  }
}

const RESUME_GUARD_KEY = 'lms.login.resumed'
// A second bounce for the same `next` inside this window means the forward
// did not stick (the target still saw no session) — stop and show the form
// rather than ping-pong between /login and the target.
const RESUME_GUARD_MS = 15_000

/**
 * Should this `?next=` arrival skip the form and forward a live session?
 *
 * The api bounces a link tap to `/login?next=<path>` when the request reached
 * it without the session cookie — e.g. the Slack MC link opened in Slack's
 * iOS in-app browser, whose first request of a fresh web view can go out
 * before stored cookies are attached. By the time this page loads the cookie
 * is there, so a signed-in person should be forwarded, not asked to sign in.
 *
 * Storage is best-effort: unavailable storage just means no loop guard.
 */
function shouldTryResume(): boolean {
  if (typeof window === 'undefined') return false
  const next = new URLSearchParams(window.location.search).get('next')
  if (!next) return false
  try {
    const raw = window.sessionStorage.getItem(RESUME_GUARD_KEY)
    const last = raw ? (JSON.parse(raw) as { next?: unknown; at?: unknown }) : null
    const recentSameTarget =
      last?.next === next && typeof last.at === 'number' && Date.now() - last.at < RESUME_GUARD_MS
    if (recentSameTarget) {
      window.sessionStorage.removeItem(RESUME_GUARD_KEY)
      return false
    }
  } catch {
    // Storage blocked or a malformed entry — proceed without the guard.
  }
  return true
}

function markResumed(): void {
  try {
    const next = new URLSearchParams(window.location.search).get('next')
    window.sessionStorage.setItem(RESUME_GUARD_KEY, JSON.stringify({ next, at: Date.now() }))
  } catch {
    // Best-effort; see shouldTryResume.
  }
}

export function LoginPage(): JSX.Element {
  const emailId = useId()
  const passwordId = useId()
  const emailErrId = `${emailId}-err`
  const passwordErrId = `${passwordId}-err`
  const formErrId = `${emailId}-form-err`

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  // Seeded from the api's OIDC-callback redirect (?error=<code>) so an
  // SSO failure lands as a visible banner instead of raw JSON.
  const [formError, setFormError] = useState<string | null>(readSsoError)
  // Checking for a live session before showing the form (see shouldTryResume).
  const [resuming, setResuming] = useState(shouldTryResume)
  const methods = useAuthMethods()
  const showMicrosoft = microsoftAuthEnabled()
  // Hide the email/password form where the api refuses password login
  // (DEV_PASSWORD_AUTH_ENABLED off, i.e. production) — but only when the
  // Microsoft button is there instead; never leave the page with no way in.
  const showPasswordForm = methods?.password !== false || !showMicrosoft

  useEffect(() => {
    if (!resuming) return
    let cancelled = false
    fetchSessionUser()
      .then(() => {
        if (cancelled) return
        markResumed()
        window.location.replace(readNextRedirect())
      })
      .catch(() => {
        // Not signed in (or the check failed) — show the form as usual.
        if (!cancelled) setResuming(false)
      })
    return () => {
      cancelled = true
    }
  }, [resuming])
  const [pending, setPending] = useState(false)
  const [microsoftPending, setMicrosoftPending] = useState(false)

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    const errors = validate(email, password)
    setFieldErrors(errors)
    setFormError(null)
    if (errors.email || errors.password) {
      return
    }

    setPending(true)
    try {
      await signInWithPassword(email.trim().toLowerCase(), password)
      // replace, not assign: drop the login form from history so Back from
      // the destination doesn't land on a sign-in page for a live session.
      window.location.replace(readNextRedirect())
    } catch (err: unknown) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        setFormError(err.message || 'Invalid email or password.')
      } else {
        setFormError(getErrorMessage(err))
      }
      setPending(false)
    }
  }

  const handleMicrosoft = async (): Promise<void> => {
    setMicrosoftPending(true)
    setFormError(null)
    try {
      await startMicrosoftLogin()
    } catch (err: unknown) {
      setFormError(getErrorMessage(err))
      setMicrosoftPending(false)
    }
  }

  // Wait for the methods check too, so production never flashes a password
  // form that then disappears.
  if (resuming || methods === null) {
    return <BootSplash />
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          background: 'var(--paper-raised)',
          // Brief: line-strong + warm-brown overlay shadow to match the
          // CommandPalette overlay register (docs/design/briefs/login.md).
          border: '1px solid var(--line-strong)',
          borderRadius: 16,
          padding: 32,
          boxShadow: '0 12px 40px rgba(80, 56, 28, 0.18)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'var(--ink-3)',
              fontWeight: 600,
              marginBottom: 8,
            }}
          >
            Leave Hub
          </div>
          <h1
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 28,
              fontWeight: 500,
              color: 'var(--ink)',
              margin: 0,
            }}
          >
            Welcome back
          </h1>
          <p
            style={{
              color: 'var(--ink-2)',
              fontSize: 14,
              marginTop: 8,
              marginBottom: 0,
            }}
          >
            {showPasswordForm
              ? 'Sign in with your work email to continue.'
              : 'Sign in with your Microsoft work account to continue.'}
          </p>
        </div>

        {formError ? (
          <div
            id={formErrId}
            role="alert"
            style={{
              marginBottom: 16,
              padding: '10px 12px',
              borderRadius: 8,
              background: 'var(--danger-bg)',
              color: 'var(--danger-fg)',
              border: '1px solid var(--danger-line)',
              fontSize: 13,
            }}
          >
            {formError}
          </div>
        ) : null}

        {showPasswordForm ? (
        <form noValidate onSubmit={(e) => void handleSubmit(e)}>
          <div style={{ marginBottom: 16 }}>
            <label
              htmlFor={emailId}
              style={{
                display: 'block',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--ink-2)',
                marginBottom: 6,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Email
            </label>
            <input
              id={emailId}
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={Boolean(fieldErrors.email)}
              aria-describedby={fieldErrors.email ? emailErrId : undefined}
              disabled={pending}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: 14,
                color: 'var(--ink)',
                background: 'var(--paper)',
                border: `1px solid ${fieldErrors.email ? 'var(--danger-line)' : 'var(--line)'}`,
                borderRadius: 8,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            {fieldErrors.email ? (
              <div
                id={emailErrId}
                style={{
                  marginTop: 6,
                  fontSize: 12,
                  color: 'var(--danger-fg)',
                }}
              >
                {fieldErrors.email}
              </div>
            ) : null}
          </div>

          <div style={{ marginBottom: 20 }}>
            <label
              htmlFor={passwordId}
              style={{
                display: 'block',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--ink-2)',
                marginBottom: 6,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Password
            </label>
            <input
              id={passwordId}
              type="password"
              autoComplete="current-password"
              required
              minLength={MIN_PASSWORD_LENGTH}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-invalid={Boolean(fieldErrors.password)}
              aria-describedby={fieldErrors.password ? passwordErrId : undefined}
              disabled={pending}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: 14,
                color: 'var(--ink)',
                background: 'var(--paper)',
                border: `1px solid ${fieldErrors.password ? 'var(--danger-line)' : 'var(--line)'}`,
                borderRadius: 8,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            {fieldErrors.password ? (
              <div
                id={passwordErrId}
                style={{
                  marginTop: 6,
                  fontSize: 12,
                  color: 'var(--danger-fg)',
                }}
              >
                {fieldErrors.password}
              </div>
            ) : null}
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={pending}
          >
            Sign in
          </Button>
        </form>
        ) : null}

        {/* Tenants without Entra hide SSO entirely —
            a disabled "coming soon" button would promise a cutover that will
            never happen there. The transition-era placeholder state is gone;
            the flag is now binary: true renders a working button, anything
            else renders nothing. Without the password form above it, the
            divider and "Or continue with" go too: it's the only way in. */}
        {showMicrosoft ? (
        <div
          style={
            showPasswordForm
              ? {
                  marginTop: 24,
                  paddingTop: 20,
                  borderTop: '1px solid var(--line-subtle)',
                  textAlign: 'center',
                }
              : { textAlign: 'center' }
          }
        >
          {showPasswordForm ? (
          <div
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'var(--ink-3)',
              marginBottom: 12,
            }}
          >
            Or continue with
          </div>
          ) : null}
          <Button
            type="button"
            variant="secondary"
            size="lg"
            fullWidth
            onClick={() => void handleMicrosoft()}
            disabled={microsoftPending}
            aria-disabled={microsoftPending}
            data-testid="microsoft-sso-button"
            loading={microsoftPending}
            iconLeft={
              <span
                aria-hidden
                style={{
                  display: 'inline-grid',
                  gridTemplateColumns: '8px 8px',
                  gridTemplateRows: '8px 8px',
                  gap: 2,
                }}
              >
                <span style={{ background: '#F25022', borderRadius: 1 }} />
                <span style={{ background: '#7FBA00', borderRadius: 1 }} />
                <span style={{ background: '#00A4EF', borderRadius: 1 }} />
                <span style={{ background: '#FFB900', borderRadius: 1 }} />
              </span>
            }
          >
            Sign in with Microsoft
          </Button>
        </div>
        ) : null}
      </div>
    </div>
  )
}
